import { useEffect, useState } from 'react';
import { Link as RouterLink, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

import { useConfirm } from 'material-ui-confirm';
import {
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
} from '@mui/material';
import Grid from 'material/components/Grid';
import { Add as AddIcon } from '@mui/icons-material';

import { colors } from 'themes';

import { deleteSignup, fetchAllSignups } from 'store/actions/signups';
import { signupsSelectors } from 'store/selectors/signups';
import { setSnackbarSuccess } from 'store/actions/global';

import { Backdrop, BreadcrumbsNavigation, Container, LinkText } from 'common/components';
import TopCard from 'common/containers/TopCard';

import { CardWrapper, CircularProgressWrapper } from 'globalStyles';

import SignupItem from '../components/SignupItem';

const Signups = () => {
  const dispatch = useDispatch();

  const { organizationUuid } = useParams();

  const breadcrumbs = [
    {
      link: `/${organizationUuid}/contacts/lists`,
      name: 'Lists',
    },
    {
      link: '',
      name: 'Contact Forms',
    },
  ];

  const { isFetching } = useSelector(state => ({
    isFetching: signupsSelectors.isFetching(state),
  }));

  const [signups, setSignups] = useState([]);

  useEffect(() => {
    dispatch(fetchAllSignups(organizationUuid)).then(data => {
      setSignups(data || []);
    });
  }, []);

  const [sortBy, setSortBy] = useState('createdAt');
  const [sortDirection, setSortDirection] = useState('desc');

  const confirm = useConfirm();

  const handleSort = column => {
    if (sortBy === column) {
      setSortDirection(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(column);
      setSortDirection('asc');
    }
  };

  const sortedSignups = [...signups].sort((a, b) => {
    let aVal;
    let bVal;

    if (sortBy === 'name') {
      aVal = a.name || '';
      bVal = b.name || '';

      return sortDirection === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
    }

    if (sortBy === 'createdAt') {
      aVal = new Date(a.createdAt || 0).getTime();
      bVal = new Date(b.createdAt || 0).getTime();
    } else if (sortBy === 'publishStatus') {
      aVal = a.publishStatus ?? 0;
      bVal = b.publishStatus ?? 0;
    } else if (sortBy === 'submissionsCount') {
      aVal = a.submissionsCount ?? 0;
      bVal = b.submissionsCount ?? 0;
    }

    return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
  });

  const headerCellSx = {
    color: colors.osloGray,
    fontSize: '14px',
    fontWeight: 500,
  };

  const handleRemoveSignup = signupUuid => {
    confirm({
      confirmationText: 'Remove',
      title: 'Remove Contact Form?',
    })
      .then(() => dispatch(deleteSignup(signupUuid)))
      .then(() => dispatch(fetchAllSignups(organizationUuid)))
      .then(data => setSignups(data || []))
      .then(() => dispatch(setSnackbarSuccess()));
  };

  return (
    <>
      <BreadcrumbsNavigation isLoading={isFetching} links={breadcrumbs} />
      <Container marginBottom="40">
        <Grid container>
          <TopCard
            title="Contact Forms"
            actionButtons={[
              {
                color: 'secondary',
                component: RouterLink,
                name: 'New Contact Form',
                startIcon: <AddIcon />,
                to: `/${organizationUuid}/create-signup`,
                variant: 'contained',
              },
            ]}
          />
        </Grid>
        <CardWrapper>
          {isFetching && (
            <CircularProgressWrapper>
              <CircularProgress />
            </CircularProgressWrapper>
          )}
          {!isFetching && !signups?.length && (
            <LinkText color="inherit" to={`/${organizationUuid}/create-signup`}>
              <span>Create your first Contact Form</span>
            </LinkText>
          )}
          {!isFetching && !!signups?.length && (
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{
                      ...headerCellSx,
                      width: '25%',
                    }}
                  >
                    <TableSortLabel
                      active={sortBy === 'name'}
                      direction={sortBy === 'name' ? sortDirection : 'asc'}
                      onClick={() => handleSort('name')}
                    >
                      Name
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={headerCellSx}>
                    <TableSortLabel
                      active={sortBy === 'createdAt'}
                      direction={sortBy === 'createdAt' ? sortDirection : 'asc'}
                      onClick={() => handleSort('createdAt')}
                    >
                      Creation Date
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={headerCellSx}>
                    <TableSortLabel
                      active={sortBy === 'publishStatus'}
                      direction={sortBy === 'publishStatus' ? sortDirection : 'asc'}
                      onClick={() => handleSort('publishStatus')}
                    >
                      Status
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={headerCellSx}>
                    <TableSortLabel
                      active={sortBy === 'submissionsCount'}
                      direction={sortBy === 'submissionsCount' ? sortDirection : 'asc'}
                      onClick={() => handleSort('submissionsCount')}
                    >
                      Submissions
                    </TableSortLabel>
                  </TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedSignups.map((signupData, index) => (
                  <SignupItem
                    createdAt={signupData.createdAt}
                    handleRemoveSignup={handleRemoveSignup}
                    index={index}
                    key={signupData.uuid}
                    name={signupData.name}
                    publishStatus={signupData.publishStatus}
                    submissionsCount={signupData.submissionsCount}
                    uuid={signupData.uuid}
                  />
                ))}
              </TableBody>
            </Table>
          )}
        </CardWrapper>
      </Container>
      <Backdrop isOpen={isFetching} />
    </>
  );
};

export default Signups;
